import Axios, {AxiosError, AxiosRequestConfig, AxiosResponse} from 'axios';
import {fetchFromStorage, removeFromStorage, saveToStorage} from '../storage';
import {identifiers} from '../constants/identifiers';
import Config from '../Config';
import {URLS} from '../constants/urls';
import {logoutUser} from '../validators/HelperFunctions';
import strings from 'common/Translation/Translate';

const axiosInstance = Axios.create({
    baseURL: Config.BaseURL,
    headers: {'Content-Type': 'application/json'},
});
axiosInstance.defaults.timeout = 300000;

let refreshPromise: Promise<string | null> | null = null;

const refreshUserContext = (accessToken: string) => {
    Axios.get(Config.BaseURL + URLS.userContext, {
        headers: {Authorization: `Bearer ${accessToken}`},
    })
        .then(ctx => {
            if (ctx.status === 200 && ctx.data?.privileges) {
                saveToStorage(identifiers.privilegeList, ctx.data.privileges);
            }
        })
        .catch(() => {
            // Privilege refresh is separate from the session. Keep the new tokens.
        });
};

const refreshAccessToken = (): Promise<string | null> => {
    if (refreshPromise) return refreshPromise;

    const refreshTokenValue = fetchFromStorage(identifiers.refresh_token);
    if (!refreshTokenValue) {
        logoutUser();
        return Promise.resolve(null);
    }

    refreshPromise = Axios.post(Config.BaseURL + URLS.refreshToken, {refreshToken: refreshTokenValue})
        .then(({data, status}) => {
            const accessToken = data?.accessToken ?? data?.access_token;
            const newRefreshToken = data?.refreshToken ?? data?.refresh_token;
            if (status !== 200 || !accessToken) {
                logoutUser();
                return null;
            }

            removeFromStorage(identifiers.access_token);
            saveToStorage(identifiers.access_token, accessToken);
            if (newRefreshToken) {
                saveToStorage(identifiers.refresh_token, newRefreshToken);
            }

            refreshUserContext(accessToken);
            return accessToken as string;
        })
        .catch(() => {
            logoutUser();
            return null;
        })
        .finally(() => {
            refreshPromise = null;
        });

    return refreshPromise;
};

function getLangParam(): string {
    try {
        const stored = fetchFromStorage(identifiers.selectedLanguage);
        if (!stored) return 'en_IN';
        const parsed = JSON.parse(stored as string);
        return parsed?.value ?? 'en_IN';
    } catch {
        return 'en_IN';
    }
}

axiosInstance.interceptors.request.use((config: AxiosRequestConfig) => {
    const token = fetchFromStorage(identifiers.access_token);
    const lang = getLangParam();
    const clonedConfig: any = config;
    const idempotencyKey =
        clonedConfig.headers?.['X-Request-Id'] || clonedConfig.headers?.['x-request-id'];

    if (token) {
        clonedConfig.headers = clonedConfig.headers || {};
        const authorization = `Bearer ${token}`;
        // Axios merges headers.common first, then the request headers. A retry still
        // carries the expired Authorization from the original call, so it must be
        // overwritten here or the retry is sent with the old token.
        clonedConfig.headers.Authorization = authorization;
        clonedConfig.headers.common = {
            ...(clonedConfig.headers.common || {}),
            Authorization: authorization,
            'Content-Type': clonedConfig.headers['Content-Type'] || clonedConfig.headers.common?.['Content-Type'] || 'application/json',
        };
        if (idempotencyKey) {
            clonedConfig.headers.common['X-Request-Id'] = idempotencyKey;
            clonedConfig.headers['X-Request-Id'] = idempotencyKey;
        }
    }
    clonedConfig.params = {
        lang: lang || 'en_IN',
        ...config.params,
    };
    return clonedConfig;
});
axiosInstance.interceptors.response.use(
    (config: AxiosResponse) => {
        return config;
    },
    (error: AxiosError) => {
        const requestUrl = error.response?.config?.url ?? '';
        const isRefreshRequest = requestUrl.includes('refresh-token') || requestUrl === URLS.refreshToken;
        const originalRequest = error.config as (AxiosRequestConfig & {_retry?: boolean}) | undefined;
        if (
            error.response &&
            error.response.status === 401 &&
            requestUrl !== URLS.login &&
            !isRefreshRequest &&
            originalRequest?._retry
        ) {
            logoutUser();
            return Promise.reject(error);
        }
        if (
            error.response &&
            error.response.status === 401 &&
            requestUrl !== URLS.login &&
            !isRefreshRequest &&
            originalRequest &&
            !originalRequest._retry
        ) {
            originalRequest._retry = true;
            return refreshAccessToken().then(token => {
                if (!token) return Promise.reject(error);
                originalRequest.headers = originalRequest.headers || {};
                (originalRequest.headers as any).Authorization = `Bearer ${token}`;
                if ((originalRequest.headers as any).common) {
                    (originalRequest.headers as any).common.Authorization = `Bearer ${token}`;
                }
                return axiosInstance(originalRequest);
            });
        } else if (
            error.response &&
            (error.code === 'ERR_NETWORK' || error.response.status === 503 || error.response.status === 502)
        ) {
            window.location.href = '#/errorPage';
        } else if (error.response && error.response.status === 413) {
            throw new Error(strings.fileSizeTooLarge);
        } else {
            return Promise.reject(error);
        }
    },
);

export default axiosInstance;
